// Portfolio Summary Types

import type { HistoricalMarkConfidence, HistoricalMarkSource } from '../../types.ts';

export interface PortfolioSummary {
  netLiq: number;
  totalMV: number;
  cash: number;
  dayPL: number;
  dayPLPct: number;
  unrealized: number;
  unrealizedPct: number;
  realizedYTD: number;
  positions: number;
  accounts: PortfolioAccountOption[];
  greeks: AggregateGreeks;
  greekBreakdown: GreekBreakdownRow[];
  sectors: SectorExposure[];
  geographies: ExposureBreakdown[];
  marketCaps: ExposureBreakdown[];
  assetClasses: AssetClassExposure[];
  topPositions: PositionSummary[];
  topConcentrationPct: number;
  correlationMatrix: CorrelationMatrix;
  equityCurve: EquityPoint[];
  equityCurveIncludingCashFlows: EquityPoint[];
  externalCashFlows: ExternalCashFlow[];
  benchmarkCurve: EquityPoint[];
  benchmarkCurveIncludingCashFlows: EquityPoint[];
  dailyPL: DailyPLPoint[];
  metrics: PortfolioMetrics;
  priceCoverage: PortfolioPriceCoverage;
  grossExposurePct: number;
  netExposurePct: number;
}

export interface PortfolioAccountOption {
  id: string;
  itemId: string;
  name: string;
  institutionName: string;
  mask: string;
  selected: boolean;
  balance?: number;
  currency?: string;
}

export interface AggregateGreeks {
  delta: number;           // Net delta (shares-equivalent)
  deltaDollar: number;     // Estimated option P&L for a $1 move in each underlying
  gamma: number;           // Net gamma
  gammaDollar: number;     // Estimated dollar gamma for a 1% underlying move
  theta: number;           // Net theta/day
  vega: number;            // Net vega
  rho: number;             // Net rho
}

export interface SectorExposure {
  name: string;
  pct: number;
  benchmarkPct: number;
  mv: number;
  color: string;
  positionCount: number;
  constituents?: ExposureConstituent[];
}

export interface ExposureBreakdown {
  name: string;
  pct: number;
  benchmarkPct: number;
  mv: number;
  color: string;
  positionCount: number;
  constituents?: ExposureConstituent[];
}

export interface ExposureConstituent {
  ticker: string;
  name: string;
  mv: number;
  pct: number;
  positionCount: number;
}

export interface AssetClassExposure {
  name: string;
  pct: number;
  mv: number;
  color: string;
}

export interface PositionSummary {
  ticker: string;
  name: string;
  sector: string;
  mv: number;
  pct: number;
  dayPL: number;
  dayPLPct: number;
  delta: number;
  theta: number;
  isOption: boolean;
  strategyType?: string;
}

export type GreekTradeType = 'equity' | 'option' | 'spread';

export interface GreekBreakdownRow {
  type: GreekTradeType;
  label: string;
  count: number;
  greeks: AggregateGreeks;
  positions: GreekPositionBreakdown[];
}

export interface GreekPositionBreakdown {
  symbol: string;
  name: string;
  underlyingSymbol: string;
  contracts: number;
  marketValue: number;
  greeks: AggregateGreeks;
  hasGreeks: boolean;
}

export interface CorrelationMatrix {
  labels: string[];
  values: (number | null)[][];
  lookbackDays: number;
}

export type PortfolioPnlUnit = 'dollar' | 'percent';
export type PortfolioPnlRange = '1M' | '3M' | 'YTD' | '1Y' | 'ALL';
export type PortfolioDensity = 'compact' | 'comfortable';

export interface PortfolioAnalysisControls {
  pnlUnit: PortfolioPnlUnit;
  pnlRange: PortfolioPnlRange;
  density: PortfolioDensity;
}

export interface PortfolioAnalysisRenderState {
  currentAnalyticsLoading?: boolean;
  currentAnalyticsMessage?: string;
  correlationLoading?: boolean;
  correlationMessage?: string;
  historicalLoading?: boolean;
  historicalMessage?: string;
}

export interface EquityPoint {
  date: string;
  value: number;
  holdings?: EquityPointHolding[];
}

export interface EquityPointHolding {
  symbol: string;
  name: string;
  type: 'equity' | 'option' | 'cash' | 'adjustment';
  quantity: number;
  value: number;
  markSource?: HistoricalMarkSource;
  markConfidence?: HistoricalMarkConfidence;
  markDate?: string;
}

export interface PortfolioPriceCoverage {
  candidateDates: number;
  renderedDates: number;
  skippedDates: number;
  skippedOptionMarkDates: number;
  totalOptionMarks: number;
  lowConfidenceOptionMarks: number;
  fallbackOptionMarks: number;
  sourceCounts: Partial<Record<HistoricalMarkSource, number>>;
  skippedSymbols: Array<{
    symbol: string;
    count: number;
  }>;
  skippedRequiredSymbols: Array<{
    symbol: string;
    count: number;
    type: 'equity' | 'option';
  }>;
}

export interface ExternalCashFlow {
  date: string;
  amount: number;
  type: string;
  description: string;
}

export interface DailyPLPoint {
  date: string;
  pnl: number;
}

export interface PortfolioMetrics {
  sharpeRatio: number | null;
  volatility: number | null;
  maxDrawdown: number | null;
  winRate: number | null;
  beta: number | null;
}

// Sector data from external API
export interface SectorData {
  symbol: string;
  sector: string;
  industry: string;
  country: string;
  marketCap?: number;
  sectorAllocations?: Record<string, number>;
  source?: string;
}

// Greeks from Massive API option snapshots
export interface OptionGreeks {
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho?: number;
  iv?: number;
}

// Extended option snapshot with Greeks
export interface OptionSnapshotWithGreeks {
  occSymbol: string;
  underlying: string;
  strike: number;
  expiration: string;
  type: 'call' | 'put';
  fmv: number | null;
  close: number | null;
  underlyingPrice: number | null;
  greeks: OptionGreeks | null;
}

// Summary state for UI updates
export interface SummaryState {
  isLoading: boolean;
  error: string | null;
  data: PortfolioSummary | null;
  expandedSections: Set<string>;
}

// Color palette for charts (matching Brian's design)
export const CHART_COLORS = {
  positive: '#22c55e',   // Green
  negative: '#ef4444',   // Red
  accent: '#0080FF',     // Blue
  muted: '#262626',
  line: '#404040',
  text: '#fafafa',
  subtle: '#a3a3a3',
  surface: '#141414',
  background: '#0a0a0a',
} as const;

export const EXPOSURE_CHART_COLORS = [
  '#c9a227',
  '#7c8ecd',
  '#3fb27f',
  '#a36bb0',
  '#d8b340',
  '#5e7bb8',
  '#d06f6f',
  '#4aa7a5',
  '#c48643',
  '#8f78d1',
  '#7fb069',
  '#d66aa5',
] as const;

export function getSectorColors(count: number): string[] {
  return Array.from({ length: count }, (_, i) => EXPOSURE_CHART_COLORS[i % EXPOSURE_CHART_COLORS.length]);
}
